import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import QuillDelta from 'quill-delta';

import * as graphql from 'generated/graphql';
import PlusIcon from 'packages/kakapo-ui-kit/assets/icons/PlusIcon';
import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';
import Tooltip from 'packages/kakapo-ui-kit/components/tooltips/Tooltip';

import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import useLanguage from 'packages/kakapo-ui-application/hooks/useLanguage';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';

import CompetencyBlockIcon from './icons/CompetencyBlockIcon';
import FinalScoreBlockIcon from './icons/FinalScoreBlockIcon';
import OpenQuestionBlockIcon from './icons/OpenQuestionBlockIcon';
import ParagraphBlockIcon from './icons/ParagraphBlockIcon';
import ReviewGoalsBlockIcon from './icons/ReviewGoalsBlockIcon';
import SentimentBlockIcon from './icons/SentimentBlockIcon';
import SignatureBlockIcon from './icons/SignatureBlockIcon';
import StarRatingBlockIcon from './icons/StarRatingBlockIcon';
import TitleBlockIcon from './icons/TitleBlockIcon';
import CreateModal from './CreateModal';
import { DirectionsIcon } from '@treams/treams-icons';

const Container = styled.div`
	padding: 10px 0px;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const Button = styled.div`
	padding: 10px 20px;
	display: flex;
	flex-direction: column;
	align-items: center;
	cursor: pointer;
	border-radius: 10px;
	position: relative;

	&:hover {
		background: rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.1);

		&:active {
			background: rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.25);
		}
	}
`;

const Icon = styled.div`
	background: ${(props) => props.theme.colors.primary};
	width: 20px;
	height: 20px;
	border-radius: 10px;

	svg {
		display: block;
	}
`;

const Relative = styled.div`
	position: relative;
`;

const Title = styled.div`
	color: ${(props) => props.theme.colors.primary};
	margin-top: 5px;
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: 70px 70px 70px 70px;
	gap: 10px;
`;

const Block = styled(Flex)`
	background: #f1f3f5;
	border-radius: 6px;
	height: 70px;
	cursor: pointer;

	&:hover {
		background: #edeeef;
	}

	&:active {
		background: #e1e2e3;
	}
`;

const BlockTitle = styled.div`
	text-align: center;
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 10px;
	margin-top: 5px;
`;

function BlockButton(props: {
	example: graphql.CreateEvaluationSectionItemInput;
	icon: React.ReactNode;
	title: string;
	onCreate: (item: graphql.CreateEvaluationSectionItemInput) => void;
}) {
	return (
		<Block
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			onClick={() => props.onCreate(props.example)}
		>
			{props.icon}
			<BlockTitle>{props.title}</BlockTitle>
		</Block>
	);
}

export default function AddButton(props: {
	existingTypes: graphql.EvaluationSectionItemType[];
	onCreate: (item: graphql.CreateEvaluationSectionItemInput) => void;
}) {
	const { t } = useTranslation();

	const [active, setActive] = useState(false);
	const [item, setItem] = useState<graphql.EvaluationSectionItem | null>(null);

	const handleCreate = (item: graphql.CreateEvaluationSectionItemInput) => {
		setActive(false);
		setItem(item as unknown as graphql.EvaluationSectionItem);
	};

	const usesDevelopmentPlan = useFeature(graphql.FeatureName.DevelopmentPlan);
	const reviewGoals = useFeature(graphql.FeatureName.Goals);

	const language = useLanguage();

	return (
		<Container>
			<Relative>
				{item && (
					<Tooltip wide grow styleContent={false} onBlur={() => setItem(null)}>
						<CreateModal
							item={item}
							onCreate={(item) => {
								props.onCreate(item as graphql.CreateEvaluationSectionItemInput);
								setItem(null);
							}}
							onCancel={() => {
								setItem(null);
							}}
						/>
					</Tooltip>
				)}
				{active && (
					<Tooltip
						title={t('common.settings.evaluationForms.questionTypes.title')}
						grow
						onBlur={() => setActive(false)}
						styleContent={false}
					>
						<Grid>
							<BlockButton
								title={t('common.settings.evaluationForms.stars.title')}
								icon={<StarRatingBlockIcon />}
								onCreate={handleCreate}
								example={{
									type: graphql.EvaluationSectionItemType.Stars,
									fillInBy: graphql.EvaluationFillInBy.Both,
									stars: {
										title: [
											{
												languageID: language.id,
												text: '',
											},
										],
									},
								}}
							/>
							<BlockButton
								title={t('common.settings.evaluationForms.openQuestion.title')}
								icon={<OpenQuestionBlockIcon />}
								onCreate={handleCreate}
								example={{
									type: graphql.EvaluationSectionItemType.OpenQuestion,
									fillInBy: graphql.EvaluationFillInBy.Both,
									openQuestion: {
										title: [
											{
												languageID: language.id,
												text: '',
											},
										],
									},
								}}
							/>
							<BlockButton
								title={t('common.settings.evaluationForms.competency.title')}
								icon={<CompetencyBlockIcon />}
								onCreate={handleCreate}
								example={{
									type: graphql.EvaluationSectionItemType.Competency,
									fillInBy: graphql.EvaluationFillInBy.Both,
									competency: {
										competency: {
											title: [
												{
													languageID: language.id,
													text: '',
												},
											],
										},
										weight: 1,
									} as unknown as graphql.EvaluationCompetencyItemInput,
								}}
							/>
							<BlockButton
								title={t('common.settings.evaluationForms.sentiment.title')}
								icon={<SentimentBlockIcon />}
								onCreate={handleCreate}
								example={{
									type: graphql.EvaluationSectionItemType.Sentiment,
									fillInBy: graphql.EvaluationFillInBy.Both,
									sentiment: {
										title: [
											{
												languageID: language.id,
												text: '',
											},
										],
										displayMethod: graphql.EvaluationSentimentDisplayMethod.Emoji,
									},
								}}
							/>
							<BlockButton
								title={t('common.settings.evaluationForms.title.title')}
								icon={<TitleBlockIcon />}
								onCreate={handleCreate}
								example={{
									type: graphql.EvaluationSectionItemType.Title,
									fillInBy: graphql.EvaluationFillInBy.Both,
									title: {
										text: [
											{
												languageID: language.id,
												text: '',
											},
										],
									},
								}}
							/>
							<BlockButton
								title={t('common.settings.evaluationForms.paragraph.title')}
								icon={<ParagraphBlockIcon />}
								onCreate={handleCreate}
								example={{
									type: graphql.EvaluationSectionItemType.Paragraph,
									fillInBy: graphql.EvaluationFillInBy.Both,
									paragraph: {
										text: [
											{
												languageID: language.id,
												text: {
													ops: [{ insert: '' }],
												} as QuillDelta,
											},
										],
									},
								}}
							/>
						</Grid>

						{!props.existingTypes.includes(graphql.EvaluationSectionItemType.ReviewGoals) ||
						!props.existingTypes.includes(graphql.EvaluationSectionItemType.FinalScore) ||
						!props.existingTypes.includes(graphql.EvaluationSectionItemType.Signature) ? (
							<Box mb={2} mt={3}>
								<Paragraph color="slateBlue">
									{t('common.settings.evaluationForms.formalActions.title')}
								</Paragraph>
								<HorizontalRule color="lightBlue" />
							</Box>
						) : null}

						<Grid>
							{!reviewGoals ||
							props.existingTypes.includes(graphql.EvaluationSectionItemType.ReviewGoals) ? null : (
								<BlockButton
									title={t('common.settings.evaluationForms.reviewGoals.title')}
									icon={<ReviewGoalsBlockIcon />}
									onCreate={handleCreate}
									example={{
										type: graphql.EvaluationSectionItemType.ReviewGoals,
										fillInBy: graphql.EvaluationFillInBy.Both,
										reviewGoals: {
											competencyLevelSetID: undefined as unknown as string,
										},
									}}
								/>
							)}
							{props.existingTypes.includes(graphql.EvaluationSectionItemType.FinalScore) ? null : (
								<BlockButton
									title={t('common.settings.evaluationForms.finalScore.title')}
									icon={<FinalScoreBlockIcon />}
									onCreate={handleCreate}
									example={{
										type: graphql.EvaluationSectionItemType.FinalScore,
										fillInBy: graphql.EvaluationFillInBy.Both,
										finalScore: {
											calculationMethod: graphql.EvaluationFinalScoreCalculationMethod.Average,
											title: [
												{
													languageID: language.id,
													text: '',
												},
											],
											competencyLevelSetID: undefined as unknown as string,
										},
									}}
								/>
							)}
							{props.existingTypes.includes(graphql.EvaluationSectionItemType.Signature) ? null : (
								<BlockButton
									title={t('common.settings.evaluationForms.signature.title')}
									icon={<SignatureBlockIcon />}
									onCreate={handleCreate}
									example={{
										type: graphql.EvaluationSectionItemType.Signature,
										fillInBy: graphql.EvaluationFillInBy.Both,
										signature: {
											title: [
												{
													languageID: language.id,
													text: '',
												},
											],
										},
									}}
								/>
							)}
							{!usesDevelopmentPlan ||
							props.existingTypes.includes(
								graphql.EvaluationSectionItemType.DevelopmentPlan,
							) ? null : (
								<BlockButton
									title={t('common.settings.evaluationForms.developmentPlan.title')}
									icon={<DirectionsIcon />}
									onCreate={handleCreate}
									example={{
										type: graphql.EvaluationSectionItemType.DevelopmentPlan,
										fillInBy: graphql.EvaluationFillInBy.Employee,
										developmentPlan: {
											title: [
												{
													languageID: language.id,
													text: t('common.settings.evaluationForms.developmentPlan.defaultTitle'),
												},
											],
										},
									}}
								/>
							)}
						</Grid>
					</Tooltip>
				)}
				<Button onClick={() => setActive(true)}>
					<Icon>
						<PlusIcon />
					</Icon>
					<Title>{t('common.settings.evaluationForms.addBlock')}</Title>
				</Button>
			</Relative>
		</Container>
	);
}
